import React, { FC } from 'react'
import classNames from 'classnames'
import omit from 'lodash/omit'
import { NavListSubheader } from 'transfix-ui/components/NavListSubheader'
import { List, makeStyles, ListProps } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'

type TOmittedProps = 'component' | 'classes' | 'disablePadding' | 'subheader'

export interface INavListProps extends Omit<ListProps, TOmittedProps> {
  /**
   * Gives the list group an optional title/header
   * @example 'Fleet Management'
   */
  subheader?: string
  /**
   * When true, hides subheader. Used when the nav is closed
   * @default false
   */
  isSubheaderHidden?: boolean
  /**
   * @default 'light'
   */
  variant?: 'light' | 'dark'
}

const useNavListStyles = makeStyles(theme => ({
  root: {
    /**
     * The following styles override styles for .nav selector
     */
    position: 'relative',
    width: '100%',
    boxShadow: 'none',
    color: 'inherit',
    height: 'auto',
    left: 0,
    margin: 0,
    padding: 0,
    right: 0,
    top: 0,
    zIndex: theme.zIndex.appBar,
    /**
     * Adds a negative bottom margin to compensate for
     * last child element, which should have 4px bottom margin
     */
    marginBottom: `-${theme.spacing(0.5)}`,
  },
  subheader: {
    color: brandColors.skyBlue1,
    marginBottom: theme.spacing(2),
  },
  variantLight: {
    backgroundColor: brandColors.transparent,
  },
  variantDark: {
    backgroundColor: brandColors.transparent,
  },
}))

/**
 * Used for styling side navigation in app
 */
export const NavList: FC<INavListProps> = ({
  children,
  isSubheaderHidden = false,
  subheader: subheaderProp,
  variant = 'light',
  ...props
}) => {
  const classes = useNavListStyles()
  const className = classNames({
    [classes.variantDark]: variant === 'dark',
    [classes.variantLight]: variant === 'light',
  })
  const subheader =
    subheaderProp && !isSubheaderHidden ? (
      <NavListSubheader disableGutters className={classes.subheader}>
        {subheaderProp}
      </NavListSubheader>
    ) : undefined

  const listClasses = omit(classes, ['variantDark', 'variantLight'])
  return (
    <List
      component='nav'
      aria-labelledby='navigation-list'
      classes={listClasses}
      className={className}
      disablePadding
      subheader={subheader}
      {...props}>
      {children}
    </List>
  )
}

export default NavList
